import React, {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

export type AppTheme = 'light' | 'dark';
export type AppLanguage = 'fr' | 'en';

type AppearanceContextType = {
  language: AppLanguage;
  setLanguage: (language: AppLanguage) => void;
  setTheme: (theme: AppTheme) => void;
  t: (key: string) => string;
  theme: AppTheme;
  toggleTheme: () => void;
};

const STORAGE_KEY = 'moranje-appearance';

const translations: Record<AppLanguage, Record<string, string>> = {
  fr: {
    administration: 'Administration',
    appearance: 'Apparence',
    chat: 'Tchat',
    clientPortfolio: 'Portefeuille client',
    darkTheme: 'Sombre',
    fichesInfos: 'Fiches et Infos',
    home: 'Accueil',
    language: 'Langue',
    lightTheme: 'Clair',
    logout: 'Déconnexion',
    theme: 'Thème',
    titlePortfolio: 'Portefeuille des titres',
    transactions: 'Gestion des Transactions',
    welcome: 'Bienvenue',
  },
  en: {
    administration: 'Administration',
    appearance: 'Appearance',
    chat: 'Chat',
    clientPortfolio: 'Client portfolio',
    darkTheme: 'Dark',
    fichesInfos: 'Sheets & Info',
    home: 'Home',
    language: 'Language',
    lightTheme: 'Light',
    logout: 'Logout',
    theme: 'Theme',
    titlePortfolio: 'Securities portfolio',
    transactions: 'Transaction Management',
    welcome: 'Welcome',
  },
};

const AppearanceContext = createContext<AppearanceContextType | undefined>(
  undefined,
);

const getInitialAppearance = (): { language: AppLanguage; theme: AppTheme } => {
  try {
    const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
    return {
      language: saved.language === 'en' ? 'en' : ('fr' as AppLanguage),
      theme: saved.theme === 'dark' ? 'dark' : ('light' as AppTheme),
    };
  } catch (error) {
    return { language: 'fr' as AppLanguage, theme: 'light' as AppTheme };
  }
};

export const AppearanceProvider = ({ children }: { children: ReactNode }) => {
  const initialAppearance = getInitialAppearance();
  const [theme, setTheme] = useState<AppTheme>(initialAppearance.theme);
  const [language, setLanguage] = useState<AppLanguage>(
    initialAppearance.language,
  );

  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    document.documentElement.lang = language;
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ language, theme }));
  }, [language, theme]);

  const value = useMemo(
    () => ({
      language,
      setLanguage,
      setTheme,
      t: (key: string) => translations[language][key] || key,
      theme,
      toggleTheme: () => setTheme(theme === 'light' ? 'dark' : 'light'),
    }),
    [language, theme],
  );

  return (
    <AppearanceContext.Provider value={value}>
      {children}
    </AppearanceContext.Provider>
  );
};

export const useAppearance = () => {
  const context = useContext(AppearanceContext);
  if (!context) {
    throw new Error('useAppearance must be used inside AppearanceProvider');
  }
  return context;
};
